import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle } from "lucide-react";
import { useScrapeUrl } from "@/hooks/useValidation";
import type {
  ValidationSource,
  ScrapeProgressStatus,
} from "@/types/validation";
import { toast } from "sonner";

interface UrlScrapeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialUrl?: string;
  /** Opens the job result viewer for the scrape job. */
  onViewRunResults?: (jobId: string) => void;
}

export function UrlScrapeDialog({
  open,
  onOpenChange,
  initialUrl = "",
  onViewRunResults,
}: UrlScrapeDialogProps) {
  const [url, setUrl] = useState(initialUrl);
  const [source, setSource] = useState<ValidationSource | "auto">("auto");
  const [dryRun, setDryRun] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState<ScrapeProgressStatus | null>(null);

  const scrapeMutation = useScrapeUrl();

  useEffect(() => {
    if (open) {
      setUrl(initialUrl);
      setShowSuccess(false);
      setJobId(null);
      setProgress(null);
    }
  }, [open, initialUrl]);

  const handleSubmit = async () => {
    if (!url.trim()) {
      toast.error("Please enter a URL");
      return;
    }

    setProgress(null);
    try {
      const result = await scrapeMutation.mutateAsync({
        url: url.trim(),
        options: {
          dry_run: dryRun,
          onStatus: (s: ScrapeProgressStatus) => setProgress(s),
        },
      });
      setShowSuccess(true);
      setJobId(result.job_id || null);
      toast.success(result.message || "Scrape started successfully");
    } catch (error) {
      toast.error(
        `Failed to start scrape: ${error instanceof Error ? error.message : "Unknown error"}`,
      );
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      if (!scrapeMutation.isPending) {
        setUrl("");
        setSource("auto");
        setDryRun(false);
        setShowSuccess(false);
        setJobId(null);
        setProgress(null);
      }
      onOpenChange(false);
    } else {
      onOpenChange(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Scrape Single URL</DialogTitle>
          <DialogDescription>
            Start a scrape for a specific product or variant URL
          </DialogDescription>
        </DialogHeader>

        {showSuccess ? (
          <div className="py-8 flex flex-col items-center justify-center gap-4">
            <CheckCircle className="w-16 h-16 text-primary" />
            <p className="text-center text-foreground">
              Scrape started successfully!
            </p>
            {jobId && (
              <p className="text-center text-sm text-muted-foreground">
                Job ID: <span className="font-mono text-primary">{jobId}</span>
              </p>
            )}
            <p className="text-center text-sm text-muted-foreground">
              Check back in a few moments for new validation results
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
              {jobId && onViewRunResults && (
                <Button onClick={() => onViewRunResults(jobId)}>
                  View run results
                </Button>
              )}
              <Button
                variant={jobId && onViewRunResults ? "outline" : "default"}
                onClick={() => handleOpenChange(false)}
              >
                Close
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label htmlFor="scrape-url">URL</Label>
              <Input
                id="scrape-url"
                placeholder="https://example.com/products/..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="mt-1"
                disabled={scrapeMutation.isPending}
              />
            </div>

            <div>
              <Label htmlFor="scrape-source">Source (optional)</Label>
              <Select
                value={source}
                onValueChange={(v) => setSource(v as ValidationSource | "auto")}
                disabled={scrapeMutation.isPending}
              >
                <SelectTrigger id="scrape-source" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto-detect</SelectItem>
                  <SelectItem value="tnvc">TNVC</SelectItem>
                  <SelectItem value="darq">DARQ</SelectItem>
                  <SelectItem value="steele">Steele</SelectItem>
                  <SelectItem value="nve">NVE</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Source will be auto-detected from the URL if not specified
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="dry-run"
                checked={dryRun}
                onCheckedChange={(checked) => setDryRun(checked === true)}
                disabled={scrapeMutation.isPending}
              />
              <Label htmlFor="dry-run" className="text-sm cursor-pointer">
                Dry run (test without saving)
              </Label>
            </div>
          </div>
        )}

        {!showSuccess && (
          <DialogFooter>
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={scrapeMutation.isPending || !url.trim()}
            >
              {scrapeMutation.isPending && (
                <Loader2 className="w-4 h-4 animate-spin" />
              )}
              {scrapeMutation.isPending && progress
                ? progress === "in_progress"
                  ? "Scrape in progress…"
                  : "Queued…"
                : "Start Scrape"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
